import {
  createSlice,
  createEntityAdapter,
  createAsyncThunk,
  EntityState,
  createSelector,
  createAction,
} from '@reduxjs/toolkit';
import axios from 'axios';
import {
  TaskListElement,
  TaskListFilters,
  TasksListTableSorting,
  TaskPermissionsMap,
} from 'types/store/tasksList';
import { SLICE_STATUS } from 'utils/constants';
import { Status } from 'utils/customTypes';
import {
  CENTRALIZED_TASKS_TABLE_TABS,
  DEFAULT_TASK_ELEMENT_PERMISSIONS,
} from 'utils/constants/centralizedTasks';
import {
  getFetchCentralizedTasksListQueryParams,
  normalizeFiltersSettingsVersions,
} from '../helpers';
import { fetchTasksList } from 'api/tasksList';
import {
  setTasksAssignees,
  setTasksProjects,
  selectAssigneesListByIds,
  selectTaskProjectById,
} from '../allTasks/allTasksSlice';
import { fetchFilterSettingByType } from 'state/Settings/Filters/FiltersSlice';
import { RootState } from 'state/store';

const teamTasksAdapter = createEntityAdapter<TaskListElement>();

interface TeamTasksState {
  tasks: EntityState<TaskListElement>;
  filters: TaskListFilters;
  sorting: TasksListTableSorting;
  status: Status;
  links: {
    next: string | null;
  };
  totalCount?: number;
  permissions: TaskPermissionsMap;
}
const initialState: TeamTasksState = {
  tasks: teamTasksAdapter.getInitialState(),
  permissions: {},
  filters: {},
  sorting: {
    sortBy: 'name',
    order: 'desc',
  },
  status: SLICE_STATUS.IDLE,
  links: {
    next: null,
  },
};

export interface FetchTasksListParams {
  fetchNext?: boolean;
  includeTotalCount?: boolean;
}

export const fetchTeamTasks = createAsyncThunk(
  'normalized/tasksList/teamTasks/fetchTeamTasks',
  async (
    { fetchNext = false, includeTotalCount = false }: FetchTasksListParams,
    { rejectWithValue, getState, dispatch }
  ) => {
    try {
      const state = getState() as RootState;
      let nextLink = null;
      if (fetchNext) {
        nextLink = state.normalized.tasksList.teamTasks.links.next;
      }
      const filters = state.normalized.tasksList.teamTasks.filters;
      const sorting = state.normalized.tasksList.teamTasks.sorting;
      const queryParams = getFetchCentralizedTasksListQueryParams(
        { nextLink, includeTotalCount },
        filters,
        sorting
      );
      const response = await fetchTasksList(queryParams);
      dispatch(setTasksAssignees(response.assignees));
      dispatch(setTasksProjects(response.projects));
      return {
        tasks: response.tasks,
        links: response.links,
        fetchNext,
        totalCount: response.totalCount,
        permissions: response.permissions,
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data);
      }
      return rejectWithValue('Error fetching team tasks');
    }
  }
);

export const updateTaskInList = createAction<{
  taskId: string;
  updatedTask: Partial<TaskListElement>;
}>('normalized/tasksList/teamTasks/updateTaskInList');

const teamTasksSlice = createSlice({
  name: 'normalized/tasksList/teamTasks',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = {
        search: state.filters.search,
        ...action.payload,
      };
    },
    setSorting: (state, action) => {
      state.sorting = action.payload;
    },
    setSearchFilter: (state, action) => {
      state.filters.search = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTeamTasks.pending, (state) => {
        state.status = SLICE_STATUS.LOADING;
      })
      .addCase(fetchTeamTasks.fulfilled, (state, action) => {
        state.status = SLICE_STATUS.IDLE;

        if (action.payload) {
          const fetchNext = action.payload.fetchNext;
          if (fetchNext) {
            teamTasksAdapter.upsertMany(state.tasks, action.payload.tasks);
            state.permissions = {
              ...state.permissions,
              ...action.payload.permissions,
            };
          } else {
            teamTasksAdapter.setAll(state.tasks, action.payload.tasks);
            state.permissions = action.payload.permissions || {};
          }
          state.links = action.payload.links;
          if (action.payload.totalCount !== undefined) {
            state.totalCount = action.payload.totalCount;
          }
        }
      })
      // Must not throw: a reducer that throws makes Immer discard the draft, so
      // the FAILED status below would never be committed and consumers watching
      // the status (useTeamTasks) would stay stuck on LOADING forever.
      .addCase(fetchTeamTasks.rejected, (state) => {
        state.status = SLICE_STATUS.FAILED;
      })
      .addCase(fetchFilterSettingByType.fulfilled, (state, action) => {
        if (
          action.payload &&
          action.payload.filter_type === CENTRALIZED_TASKS_TABLE_TABS.TEAM_TASKS
        ) {
          state.filters = normalizeFiltersSettingsVersions(
            action.payload.filter_settings
          );
        }
      })
      .addCase(updateTaskInList, (state, action) => {
        if (action.payload) {
          const { taskId, updatedTask } = action.payload;
          const existingTask = teamTasksAdapter
            .getSelectors()
            .selectById(state.tasks, taskId);
          if (existingTask) {
            teamTasksAdapter.updateOne(state.tasks, {
              id: taskId,
              changes: updatedTask,
            });
          }
        }
      });
  },
});

const { selectAll: selectAllTeamTasks } = teamTasksAdapter.getSelectors(
  (state: RootState) => state.normalized.tasksList.teamTasks.tasks
);

const selectTaskPermissions = (state: RootState) =>
  state.normalized.tasksList.teamTasks.permissions;

export const selectFetchTeamTasksStatus = (state: RootState) =>
  state.normalized.tasksList.teamTasks.status;

export const selectTeamTasks = createSelector(
  [(state: RootState) => state, selectAllTeamTasks, selectTaskPermissions],
  (state, tasks, permissions) => {
    return tasks.map((task: TaskListElement) => ({
      ...task,
      assignees: selectAssigneesListByIds(state, task.assignees),
      project: selectTaskProjectById(state, task.projectId),
      permissions: permissions[task.id] ?? DEFAULT_TASK_ELEMENT_PERMISSIONS,
    }));
  }
);

export const selectTeamTasksTableFilters = (state: RootState) =>
  state.normalized.tasksList.teamTasks.filters;

export const selectTeamTasksSorting = (state: RootState) =>
  state.normalized.tasksList.teamTasks.sorting;

export const selectCanFetchMoreTeamTasks = (state: RootState) =>
  !!state.normalized.tasksList.teamTasks.links.next;

export const selectTeamTasksTotalCount = (state: RootState) =>
  state.normalized.tasksList.teamTasks.totalCount;

export const { setFilters, setSorting, setSearchFilter } =
  teamTasksSlice.actions;

export default teamTasksSlice.reducer;
